import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Mail, 
  Search, 
  RefreshCw, 
  Eye, 
  Send, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  MailOpen,
  ChevronLeft,
  ChevronRight,
  Filter,
  BarChart3,
  Loader2,
  Calendar,
  User,
  FileText,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const EMAIL_TYPE_LABELS = {
  'general': 'Allgemein',
  'appointment_confirmation': 'Terminbestätigung',
  'appointment_reminder': 'Terminerinnerung',
  'appointment_cancelled': 'Stornierung',
  'payment_reminder': 'Zahlungserinnerung',
  'dunning': 'Mahnung',
  'review_request': 'Bewertungsanfrage',
  'password_reset': 'Passwort-Reset',
  'welcome': 'Willkommen',
  'document': 'Dokument',
  'custom': 'Individuelle E-Mail',
  'resent': 'Erneut gesendet'
};

const STATUS_CONFIG = {
  'sent': { label: 'Gesendet', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  'opened': { label: 'Geöffnet', color: 'bg-blue-100 text-blue-700', icon: MailOpen },
  'error': { label: 'Fehler', color: 'bg-red-100 text-red-700', icon: XCircle },
  'pending': { label: 'Ausstehend', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  'skipped': { label: 'Übersprungen', color: 'bg-gray-100 text-gray-700', icon: AlertTriangle }
};

const AdminEmailLogs = () => {
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [resending, setResending] = useState(null);

  const loadEmails = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('email_type', typeFilter);
      
      const response = await api.get(`/admin/email-logs?${params}`);
      setEmails(response.data.emails || []);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error loading emails:', error);
      toast.error('Fehler beim Laden der E-Mails');
    }
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/admin/email-stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  useEffect(() => {
    loadEmails();
    loadStats();
  }, [page, statusFilter, typeFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadEmails();
  };

  const viewEmail = async (emailId) => {
    try {
      const response = await api.get(`/admin/email-logs/${emailId}`);
      setSelectedEmail(response.data);
      setShowEmailDialog(true);
    } catch (error) {
      toast.error('Fehler beim Laden der E-Mail');
    }
  };

  const resendEmail = async (emailId) => {
    setResending(emailId);
    try {
      await api.post(`/admin/email-logs/${emailId}/resend`);
      toast.success('E-Mail wurde erneut gesendet');
      loadEmails();
      loadStats();
    } catch (error) {
      toast.error('Fehler beim erneuten Senden');
    }
    setResending(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusDisplay = (email) => {
    const hasOpened = email.opened_at !== null;
    const status = hasOpened ? 'opened' : email.status;
    const config = STATUS_CONFIG[status] || STATUS_CONFIG['pending'];
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {hasOpened ? `Geöffnet (${email.opened_count}x)` : config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 -m-4 lg:-m-8 p-4 sm:p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="bg-gradient-to-r from-[#9B1B30] to-[#c42847] rounded-xl p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
                <Mail className="w-8 h-8" />
                Postausgang
              </h1>
              <p className="text-white/80 mt-1 text-sm sm:text-base">Alle ausgehenden E-Mails mit Empfangsbestätigung</p>
            </div>
            <Button 
              onClick={() => { loadEmails(); loadStats(); }}
              className="bg-white/20 hover:bg-white/30 text-white border border-white/30"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Aktualisieren
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="max-w-7xl mx-auto mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                  <p className="text-xs text-gray-500">Gesamt</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.sent}</p>
                  <p className="text-xs text-gray-500">Gesendet</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <MailOpen className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.opened}</p>
                  <p className="text-xs text-gray-500">Geöffnet ({stats.open_rate}%)</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.errors}</p>
                  <p className="text-xs text-gray-500">Fehler</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="max-w-7xl mx-auto mb-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="E-Mail, Betreff oder Kundenname suchen..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="sent">Gesendet</SelectItem>
                <SelectItem value="error">Fehler</SelectItem>
                <SelectItem value="skipped">Übersprungen</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="E-Mail-Typ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Typen</SelectItem>
                {Object.entries(EMAIL_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="submit" className="bg-[#9B1B30] hover:bg-[#7d1526] text-white">
              <Filter className="w-4 h-4 mr-2" />
              Filtern
            </Button>
          </form>
        </div>
      </div>

      {/* Email List */}
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#9B1B30] mx-auto mb-3" />
              <p className="text-gray-500">E-Mails werden geladen...</p>
            </div>
          ) : emails.length === 0 ? (
            <div className="p-12 text-center">
              <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">Keine E-Mails gefunden</p>
              <p className="text-gray-400 text-sm mt-1">Versendete E-Mails erscheinen hier automatisch</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="sm:hidden divide-y divide-gray-100">
                {emails.map((email) => (
                  <div key={email.id} className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{email.to_email}</p>
                        {email.customer_name && (
                          <p className="text-sm text-gray-500 truncate">{email.customer_name}</p>
                        )}
                      </div>
                      {getStatusDisplay(email)}
                    </div>
                    <p className="text-sm text-gray-700 mb-2 line-clamp-2">{email.subject}</p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{formatDate(email.created_at)}</span>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => viewEmail(email.id)}
                          className="h-8 px-2"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => resendEmail(email.id)}
                          disabled={resending === email.id}
                          className="h-8 px-2"
                        >
                          {resending === email.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Empfänger</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Betreff</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Typ</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Gesendet</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Geöffnet</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {emails.map((email) => (
                      <tr key={email.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{email.to_email}</p>
                            {email.customer_name && (
                              <p className="text-xs text-gray-500">{email.customer_name}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-700 max-w-xs truncate">{email.subject}</p>
                          {email.has_attachments && (
                            <span className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                              <FileText className="w-3 h-3" />
                              {email.attachment_count} Anhang
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                            {EMAIL_TYPE_LABELS[email.email_type] || email.email_type || 'Allgemein'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {getStatusDisplay(email)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatDate(email.sent_at || email.created_at)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {email.opened_at ? (
                            <span className="text-green-600">
                              {formatDate(email.opened_at)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => viewEmail(email.id)}
                              className="h-8 w-8 p-0"
                              title="Ansehen"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => resendEmail(email.id)}
                              disabled={resending === email.id}
                              className="h-8 w-8 p-0"
                              title="Erneut senden"
                            >
                              {resending === email.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 bg-gray-50">
                <p className="text-sm text-gray-600">
                  Zeige {((page - 1) * 20) + 1}-{Math.min(page * 20, total)} von {total} E-Mails
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="flex items-center px-3 text-sm text-gray-600">
                    Seite {page} von {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Email Preview Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#9B1B30]" />
              E-Mail Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedEmail && (
            <div className="space-y-4">
              {/* Email Meta */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">Empfänger</Label>
                    <p className="font-medium text-gray-900">{selectedEmail.to_email}</p>
                    {selectedEmail.customer_name && (
                      <p className="text-sm text-gray-600">{selectedEmail.customer_name}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Status</Label>
                    <div className="mt-1">{getStatusDisplay(selectedEmail)}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Gesendet am</Label>
                    <p className="text-gray-900">{formatDate(selectedEmail.sent_at || selectedEmail.created_at)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Geöffnet am</Label>
                    <p className="text-gray-900">
                      {selectedEmail.opened_at ? (
                        <span className="text-green-600">
                          {formatDate(selectedEmail.opened_at)} ({selectedEmail.opened_count}x)
                        </span>
                      ) : (
                        <span className="text-gray-400">Noch nicht geöffnet</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Subject */}
              <div>
                <Label className="text-xs text-gray-500">Betreff</Label>
                <p className="font-semibold text-gray-900 text-lg">{selectedEmail.subject}</p>
              </div>

              {/* Email Content Preview */}
              <div>
                <Label className="text-xs text-gray-500 mb-2 block">Inhalt</Label>
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                  <iframe
                    srcDoc={selectedEmail.html_content}
                    className="w-full h-[400px] border-0"
                    title="E-Mail Vorschau"
                    sandbox="allow-same-origin"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => setShowEmailDialog(false)}
                >
                  Schließen
                </Button>
                <Button
                  onClick={() => {
                    resendEmail(selectedEmail.id);
                    setShowEmailDialog(false);
                  }}
                  className="bg-[#9B1B30] hover:bg-[#7d1526] text-white"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Erneut senden
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEmailLogs;
